import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Lightning, Rocket, Crown } from '@phosphor-icons/react';
import PaymentModal from '../components/PaymentModal';

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 0,
    priceLabel: 'Free',
    icon: Lightning,
    description: 'Try DeFiBot free for 7 days. No payment required.',
    badge: '7-Day Free Trial',
    features: [
      { text: 'AI Trading Agent', included: true },
      { text: '3 trading pairs per network', included: true },
      { text: 'Basic arbitrage scanner', included: true },
      { text: 'Email support', included: true },
      { text: 'Profit calculator', included: true },
      { text: 'MEV scanner', included: false },
      { text: 'API access', included: false },
      { text: 'Priority support', included: false },
    ],
    popular: false,
  },
  {
    id: 'pro',
    name: 'Professional',
    price: 500,
    priceLabel: '$500',
    icon: Rocket,
    description: 'Full power for serious traders seeking maximum profit.',
    features: [
      { text: 'AI Trading Agent (all modes)', included: true },
      { text: 'Unlimited trading pairs', included: true },
      { text: 'MEV + Arbitrage + Sniper', included: true },
      { text: 'Priority support', included: true },
      { text: 'Live profit calculator', included: true },
      { text: 'API access (100K calls/mo)', included: true },
      { text: 'Advanced presets & config', included: true },
      { text: 'Multi-network bot', included: true },
    ],
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 1000,
    priceLabel: '$1000',
    icon: Crown,
    description: 'Institutional-grade tools for professional traders & funds.',
    features: [
      { text: 'Everything in Professional', included: true },
      { text: 'Unlimited API access', included: true },
      { text: '24/7 dedicated support', included: true },
      { text: 'Custom strategy development', included: true },
      { text: 'Custom analytics dashboard', included: true },
      { text: 'White-label solution', included: true },
      { text: 'Private transaction relay', included: true },
      { text: 'Dedicated server node', included: true },
    ],
    popular: false,
  },
];

const Pricing = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setPaymentModalOpen(true);
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-medium">
            Pricing
          </span>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-[var(--text-primary)] mt-4 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
            Start free for 7 days. Upgrade anytime with USDT or USDC on any supported network. All plans include lifetime access.
          </p>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative p-8 rounded-2xl border transition-all ${
                plan.popular
                  ? 'bg-gradient-to-b from-[var(--color-primary)]/10 to-[var(--bg-surface-1)] border-[var(--color-primary)]/50 shadow-[0_0_40px_rgba(0,229,255,0.15)]'
                  : 'bg-[var(--bg-surface-1)] border-[var(--border-default)] hover:border-[var(--color-primary)]/30'
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.badge && (
                <div className="absolute -top-4 right-5">
                  <span className="px-3 py-1 bg-[var(--accent-success)] text-[var(--bg-primary)] text-xs font-bold rounded-full">
                    {plan.badge}
                  </span>
                </div>
              )}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="px-4 py-1 bg-[var(--color-primary)] text-[var(--bg-primary)] text-sm font-semibold rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  plan.popular ? 'bg-[var(--color-primary)]/20' : 'bg-[var(--bg-surface-2)]'
                }`}>
                  <plan.icon 
                    size={24} 
                    className={plan.popular ? 'text-[var(--color-primary)]' : 'text-[var(--text-secondary)]'} 
                    weight="bold" 
                  />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[var(--text-primary)]">
                    {plan.name}
                  </h3>
                </div>
              </div>

              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {plan.description}
              </p>

              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  {plan.price === 0 ? (
                    <span className="text-4xl font-bold text-[var(--accent-success)]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>FREE</span>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-[var(--text-primary)]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${plan.price}</span>
                      <span className="text-[var(--text-secondary)]">USDT/USDC</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  {plan.price === 0 ? '7-day trial • No payment needed' : 'One-time payment • Lifetime access'}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    {feature.included ? (
                      <Check size={20} className="text-[var(--accent-success)] mt-0.5" weight="bold" />
                    ) : (
                      <X size={20} className="text-[var(--text-secondary)]/40 mt-0.5" weight="bold" />
                    )}
                    <span className={feature.included ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]/50'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleSelectPlan(plan)}
                className={`w-full py-4 rounded-xl font-semibold transition-all ${
                  plan.popular
                    ? 'bg-[var(--color-primary)] text-[var(--bg-primary)] hover:shadow-[0_0_20px_var(--border-glow)]'
                    : 'bg-[var(--bg-surface-2)] text-[var(--text-primary)] border border-[var(--border-default)] hover:border-[var(--color-primary)]/50'
                }`}
                data-testid={`select-plan-${plan.id}`}
              >
                Get Started
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* FAQ Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-[var(--text-secondary)]">
            Have questions?{' '}
            <a href="/faq" className="text-[var(--color-primary)] hover:underline">
              Check our FAQ
            </a>{' '}
            or{' '}
            <a href="/contact" className="text-[var(--color-primary)] hover:underline">
              contact us
            </a>
          </p>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-16 p-8 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-[var(--text-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>
                50K+
              </p>
              <p className="text-sm text-[var(--text-secondary)]">Active Users</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[var(--text-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>
                $847M+
              </p>
              <p className="text-sm text-[var(--text-secondary)]">Trading Volume</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[var(--text-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>
                94.7%
              </p>
              <p className="text-sm text-[var(--text-secondary)]">Success Rate</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[var(--text-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>
                4.9/5
              </p>
              <p className="text-sm text-[var(--text-secondary)]">User Rating</p>
            </div>
          </div>
        </motion.div>
      </div>

      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        plan={selectedPlan}
      />
    </div>
  );
};

export default Pricing;
